import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Skeleton, useTheme } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { glass, ADMIN_GRADIENTS, ADMIN_COLORS } from './glassTokens';

/**
 * Count-up hook — animates a number from 0 to `target` over `duration` ms.
 */
function useCountUp(target, duration = 1000, skip = false) {
  const [value, setValue] = useState(skip ? target : 0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (skip) { setValue(target); return; }
    const start = performance.now();
    const from = 0;
    const to = Number(target) || 0;
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out-quart
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.round(from + (to - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration, skip]);

  return value;
}

const ICON_GRADIENTS = [
  ADMIN_GRADIENTS.accentShort,
  'linear-gradient(135deg, #8B5CF6 0%, #22D3EE 100%)',
  ADMIN_GRADIENTS.success,
  'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
];

/**
 * StatCard — KPI tile with count-up value, delta chip, icon, and skeleton state.
 *
 * Props:
 *   label        string   — metric label
 *   value        number   — metric value
 *   prefix       string   — e.g. '₹'
 *   suffix       string   — e.g. '%'
 *   delta        string   — e.g. '+12%' (null hides chip)
 *   deltaPositive bool    — green vs red delta
 *   icon         node     — MUI icon element
 *   iconIndex    0-3      — chooses icon gradient
 *   loading      bool     — shows skeleton when true
 *   sparklineSlot node    — optional sparkline rendered below
 *   index        number   — stagger index
 */
export default function StatCard({
  label = '',
  value = 0,
  prefix = '',
  suffix = '',
  delta = null,
  deltaPositive = true,
  icon = null,
  iconIndex = 0,
  loading = false,
  sparklineSlot = null,
  index = 0,
}) {
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const displayValue = useCountUp(value, 900, reducedMotion || loading);

  const mountVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.04, duration: 0.32, ease: 'easeOut' },
    }),
  };

  const iconGrad = ICON_GRADIENTS[iconIndex % ICON_GRADIENTS.length];

  return (
    <Box
      component={motion.div}
      custom={index}
      variants={mountVariants}
      initial="hidden"
      animate="visible"
      whileHover={reducedMotion ? {} : { y: -2 }}
      transition={{ duration: 0.18 }}
      sx={{
        ...g.surface,
        borderRadius: 3,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        // gradient top-edge ring
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: iconGrad,
          opacity: 0.8,
        },
      }}
    >
      {loading ? (
        <Box>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={44} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width={60} height={22} />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums',
                  color: isDark ? '#F8FAFC' : '#0F172A',
                  lineHeight: 1.1,
                }}
              >
                {prefix}{displayValue.toLocaleString()}{suffix}
              </Typography>
            </Box>

            {icon && (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: iconGrad,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                  ml: 2,
                  boxShadow: `0 4px 12px rgba(99,102,241,0.3)`,
                }}
              >
                {icon}
              </Box>
            )}
          </Box>

          {delta && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mt: 1.5,
                px: 1,
                py: 0.25,
                borderRadius: '6px',
                fontSize: '0.72rem',
                fontWeight: 600,
                background: deltaPositive
                  ? 'rgba(16,185,129,0.15)'
                  : 'rgba(244,63,94,0.15)',
                color: deltaPositive ? ADMIN_COLORS.success : ADMIN_COLORS.danger,
              }}
            >
              {delta}
            </Box>
          )}

          {sparklineSlot && (
            <Box mt={1.5}>{sparklineSlot}</Box>
          )}
        </>
      )}
    </Box>
  );
}
